// =============================================================
// PLAN CONFIGURATION - CoreGrowth Pricing Lab
// =============================================================

export type PlanTier = 'free' | 'starter' | 'pro' | 'enterprise';

export type BillingCycle = 'monthly' | 'yearly';

// Preise in CHF
export const PLAN_PRICES: Record<PlanTier, { monthly: number; yearly: number }> = {
  free: { monthly: 0, yearly: 0 },
  starter: { monthly: 49, yearly: 470 },  // ~39/Mo = 20% Rabatt
  pro: { monthly: 99, yearly: 950 },       // ~79/Mo = 20% Rabatt
  enterprise: { monthly: 249, yearly: 2390 }, // ~199/Mo = 20% Rabatt
};

// Limits pro Plan
export const PLAN_LIMITS: Record<PlanTier, {
  scenariosPerMonth: number;
  productsServices: number;
  users: number;
  historyDays: number;
  parallelComparisons: number;
  aiQuestionsPerMonth: number;
}> = {
  free: {
    scenariosPerMonth: 5,
    productsServices: 3,
    users: 1,
    historyDays: 7,
    parallelComparisons: 1,
    aiQuestionsPerMonth: 0,
  },
  starter: {
    scenariosPerMonth: 30,
    productsServices: 20,
    users: 3,
    historyDays: 90,
    parallelComparisons: 2,
    aiQuestionsPerMonth: 10,
  },
  pro: {
    scenariosPerMonth: -1, // unlimited
    productsServices: 100,
    users: 10,
    historyDays: 365,
    parallelComparisons: 5,
    aiQuestionsPerMonth: -1, // unlimited
  },
  enterprise: {
    scenariosPerMonth: -1,
    productsServices: -1,
    users: -1,
    historyDays: -1,
    parallelComparisons: -1,
    aiQuestionsPerMonth: -1,
  },
};

// Feature-Zugang pro Plan
export const PLAN_FEATURES: Record<string, PlanTier> = {
  // Free
  basic_simulation: 'free',
  
  // Starter
  export_pdf: 'starter',
  ai_basic: 'starter',
  scenario_history: 'starter',
  
  // Pro
  export_excel: 'pro',
  export_csv: 'pro',
  ai_advanced: 'pro',
  proactive_alerts: 'pro',
  industry_benchmarks: 'pro',
  what_if_analysis: 'pro',
  team_sharing: 'pro',
  team_comments: 'pro',
  pricing_copilot_unlimited: 'pro',
  
// Enterprise
  api_access: 'enterprise',
  sso_saml: 'enterprise',
  sso_oidc: 'enterprise',
  white_label: 'enterprise',
  sla_guarantee: 'enterprise',
  dedicated_support: 'enterprise',
  custom_integrations: 'enterprise',
  roles_permissions: 'enterprise',
  
  // Product Materials (Tiered)
  product_materials_basic: 'starter',     // Materialien zuordnen + Mengen
  product_materials_pricing: 'pro',       // Custom-Preise + Kostenberechnung
  product_materials_sync: 'enterprise',   // Auto-Sync zu base_cost_material
};

// Plan-Metadaten für UI
export const PLAN_METADATA: Record<PlanTier, {
  name: string;
  description: string;
  badge?: string;
  isPopular?: boolean;
  ctaText: string;
  ctaVariant: 'outline' | 'default' | 'secondary';
}> = {
  free: {
    name: 'Free',
    description: 'Zum Kennenlernen',
    ctaText: 'Kostenlos starten',
    ctaVariant: 'outline',
  },
  starter: {
    name: 'Starter',
    description: 'Für den Einstieg',
    ctaText: 'Starter wählen',
    ctaVariant: 'outline',
  },
  pro: {
    name: 'Pro',
    description: 'Für wachsende Teams',
    badge: 'Beliebt',
    isPopular: true,
    ctaText: 'Pro wählen',
    ctaVariant: 'default',
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Für besondere Anforderungen',
    ctaText: 'Kontakt aufnehmen',
    ctaVariant: 'secondary',
  },
};

// Tier-Hierarchie für Vergleiche
export const TIER_HIERARCHY: Record<PlanTier, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  enterprise: 3,
};

// Helper: Kann User auf Feature zugreifen?
export function canAccessFeature(userTier: PlanTier, feature: string): boolean {
  const requiredTier = PLAN_FEATURES[feature];
  if (!requiredTier) return true; // Feature nicht definiert = erlaubt
  return TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[requiredTier];
}

// Helper: Ist Limit erreicht?
export function isLimitReached(
  userTier: PlanTier,
  limitType: keyof typeof PLAN_LIMITS['free'],
  currentUsage: number
): boolean {
  const limit = PLAN_LIMITS[userTier][limitType];
  if (limit === -1) return false; // unlimited
  return currentUsage >= limit;
}

// Helper: Preis formatieren
export function formatPrice(tier: PlanTier, cycle: BillingCycle): string {
  const price = PLAN_PRICES[tier][cycle];
  if (price === 0) return 'Kostenlos';
  
  if (cycle === 'yearly') {
    const monthlyEquivalent = Math.round(price / 12);
    return `CHF ${monthlyEquivalent}`;
  }
  return `CHF ${price}`;
}

// Helper: Ersparnis berechnen
export function calculateSavings(tier: PlanTier): number {
  const monthly = PLAN_PRICES[tier].monthly;
  const yearly = PLAN_PRICES[tier].yearly;
  if (monthly === 0) return 0;
  const yearlyIfMonthly = monthly * 12;
  return Math.round(((yearlyIfMonthly - yearly) / yearlyIfMonthly) * 100);
}

// ============================================================
// LEGACY EXPORTS - für Abwärtskompatibilität
// ============================================================

// Mapping von altem PlanType auf neues PlanTier
export type PlanType = PlanTier | 'trial';

// Legacy Feature-Keys für bestehenden Code
export const ALL_FEATURE_KEYS = [
  'basic_simulation',
  'export_pdf',
  'ai_basic',
  'scenario_history',
  'export_excel',
  'export_csv',
  'ai_advanced',
  'proactive_alerts',
  'industry_benchmarks',
  'what_if_analysis',
  'team_sharing',
  'team_comments',
  'pricing_copilot_unlimited',
  'api_access',
  'sso_saml',
  'sso_oidc',
  'white_label',
  'sla_guarantee',
  'dedicated_support',
  'custom_integrations',
  'roles_permissions',
  // Legacy feature keys
  'scenario_wizard',
  'scenario_assistant_basic',
  'simulation_kpi',
  'management_summary',
  'decision_summary_basic',
  'service_upgrades_basic',
  'outcome_tracking',
  'ai_analysis_basic',
  'pdf_export',
  'trend_charts',
  'scenario_comparison',
  'ai_analysis',
  'ai_analysis_context',
  'decision_summary_enhanced',
  'scenario_templates_basic',
  'scenario_assistant_advanced',
  'multi_user',
  'service_upgrades_custom',
  'uncertainty_analysis',
  'outcome_calibration',
  'alerts_engine',
  'pricing_navigator',
  'api_cost_driver_sources',
  'exec_dashboard',
  'scenario_templates_advanced',
  'advanced_elasticity',
  'trend_history_extended',
  // Product Materials
  'product_materials_basic',
  'product_materials_pricing',
  'product_materials_sync',
] as const;

export type FeatureKey = typeof ALL_FEATURE_KEYS[number];

// Legacy PlanLimits Interface
export interface PlanLimits {
  scenarios_per_month: number | null;
  users: number | null;
  retention_months: number | null;
  scenario_comparison_limit: number | null;
}

// Legacy PlanConfig Interface
export interface PlanConfig {
  label: string;
  limits: PlanLimits;
  features: FeatureKey[];
  inherit_from?: PlanType;
}

// Legacy PLAN_CONFIG für Abwärtskompatibilität
export const PLAN_CONFIG: Record<PlanType, PlanConfig> = {
  free: {
    label: 'Free',
    limits: {
      scenarios_per_month: 5,
      users: 1,
      retention_months: 1,
      scenario_comparison_limit: null,
    },
    features: ['basic_simulation', 'scenario_wizard', 'simulation_kpi'],
  },
  starter: {
    label: 'Starter',
    limits: {
      scenarios_per_month: 30,
      users: 3,
      retention_months: 3,
      scenario_comparison_limit: 2,
    },
    features: [
      'basic_simulation',
      'scenario_wizard',
      'scenario_assistant_basic',
      'simulation_kpi',
      'management_summary',
      'decision_summary_basic',
      'export_csv',
      'service_upgrades_basic',
      'outcome_tracking',
      'ai_analysis_basic',
      'export_pdf',
      'ai_basic',
      'scenario_history',
      'product_materials_basic',
    ],
  },
  pro: {
    label: 'Pro',
    limits: {
      scenarios_per_month: null,
      users: 10,
      retention_months: 12,
      scenario_comparison_limit: 5,
    },
    features: [
      'basic_simulation',
      'scenario_wizard',
      'scenario_assistant_basic',
      'simulation_kpi',
      'management_summary',
      'decision_summary_basic',
      'export_csv',
      'service_upgrades_basic',
      'outcome_tracking',
      'ai_analysis_basic',
      'export_pdf',
      'ai_basic',
      'scenario_history',
      'pdf_export',
      'trend_charts',
      'scenario_comparison',
      'ai_analysis',
      'ai_analysis_context',
      'decision_summary_enhanced',
      'scenario_templates_basic',
      'scenario_assistant_advanced',
      'multi_user',
      'service_upgrades_custom',
      'uncertainty_analysis',
      'outcome_calibration',
      'alerts_engine',
      'pricing_navigator',
      'api_cost_driver_sources',
      'export_excel',
      'ai_advanced',
      'proactive_alerts',
      'industry_benchmarks',
      'what_if_analysis',
      'team_sharing',
      'team_comments',
      'pricing_copilot_unlimited',
      'product_materials_basic',
      'product_materials_pricing',
    ],
  },
  enterprise: {
    label: 'Enterprise',
    limits: {
      scenarios_per_month: null,
      users: null,
      retention_months: null,
      scenario_comparison_limit: null,
    },
    features: [...ALL_FEATURE_KEYS],
  },
  trial: {
    label: 'Pro-Trial',
    inherit_from: 'pro',
    limits: {
      scenarios_per_month: 20,
      users: 5,
      retention_months: 12,
      scenario_comparison_limit: 3,
    },
    features: [],
  },
};

// Legacy Helper-Funktionen
export function getResolvedFeatures(plan: PlanType): FeatureKey[] {
  const config = PLAN_CONFIG[plan];
  if (config.inherit_from) {
    return PLAN_CONFIG[config.inherit_from].features;
  }
  return config.features;
}

export function isPlanFeature(plan: PlanType, feature: FeatureKey): boolean {
  return getResolvedFeatures(plan).includes(feature);
}

export function getMinimumPlanForFeature(feature: FeatureKey): PlanType {
  if (PLAN_CONFIG.free.features.includes(feature)) return 'free';
  if (PLAN_CONFIG.starter.features.includes(feature)) return 'starter';
  if (PLAN_CONFIG.pro.features.includes(feature)) return 'pro';
  return 'enterprise';
}

export function getUpgradeMessage(feature: FeatureKey): string {
  const requiredPlan = getMinimumPlanForFeature(feature);
  return `Upgrade auf ${PLAN_CONFIG[requiredPlan].label} für dieses Feature`;
}
